import { InvalidLinkCause } from './deepLink';

/**
 * The Feedback_Page's own observability (Requirement 18 criterion 12).
 *
 * This exists because every other counter in the feature begins at or after the form-open, and four of
 * the page's five outcomes occur *before* it. A ticket page that renders no form produces no form-open,
 * so it never registers as an unfilled open — Fill_Rate does not fall, it goes empty, and the health
 * indicator becomes uninformative exactly when the surface breaks.
 *
 * There is no client-side metrics SDK in this website: nothing but `usePutCwMetrics`, which is a
 * `useQuery` that fires as a side effect of rendering and is served from the Apollo cache on a repeat
 * with identical variables. Neither property is acceptable for a counter — one makes emission a
 * function of render rather than of outcome, the other silently drops the second occurrence. So the
 * page depends on the interface below and the transport is injected. The default posts to a BFF route
 * that emits the datum server-side, alongside the rest of this feature's metrics.
 */

/**
 * Every settled state the page can reach, including `renderable` — which is emitted too, so the others
 * are rates rather than bare counts.
 *
 * `analysis-not-found` and `analysis-unavailable` are separate because they need separate alarms: the
 * first is a normal outcome for an unanalysed ticket and its rate is a product signal, while the second
 * only happens when the memory read fails and its rate is an availability signal. Summed together,
 * neither is legible.
 */
export type PageOutcome =
  | 'renderable'
  | 'named-missing-parameter'
  | 'invalid-link'
  | 'expired-link'
  | 'analysis-not-found'
  | 'analysis-unavailable';

/** How the context read settled. */
export type ContextResolutionOutcome =
  | 'events'
  | 'empty'
  | 'error'
  | 'timed-out';

export interface PageOutcomeEvent {
  outcome: PageOutcome;
  /** Present only on `invalid-link`. A closed set, so it is safe as a dimension. */
  cause?: InvalidLinkCause;
  /** Present only on `named-missing-parameter`. Also a closed set — the six contract parameters. */
  missingParameter?: string;
}

export interface ContextResolutionEvent {
  outcome: ContextResolutionOutcome;
  durationMs: number;
}

/**
 * Both emissions, as one injected dependency.
 *
 * Best-effort by contract: an implementation must not throw and must not make the caller wait. A page
 * that fails to render because telemetry is down is a worse outcome than a missing datum.
 */
export interface MerlinFeedbackTelemetry {
  emitPageOutcome: (event: PageOutcomeEvent) => void;
  emitContextResolution: (event: ContextResolutionEvent) => void;
}

/**
 * Deliberately carries **no** `sessionId`, Ticket_Id, account id or domain name (criterion 12d).
 *
 * Those are per-account, per-domain and per-ticket values, so a dimension keyed on any of them takes
 * one value per entity and becomes costly and unqueryable at fleet cardinality — and it would put
 * ticket identifiers into telemetry that criterion 7 keeps ticket content out of. When a mismatch needs
 * diagnosing, the values go to a log record, which is what `logPath` below is for: it identifies the
 * request without becoming a dimension.
 */
const PAGE_OUTCOME_ROUTE = '/api/agentHawk/merlin-feedback/page-outcome';
const CONTEXT_RESOLUTION_ROUTE =
  '/api/agentHawk/merlin-feedback/context-resolution';

function postBestEffort(route: string, body: unknown): void {
  try {
    const request = fetch(route, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      /*
       * `include`, for the reason given in `resolveContextClient.ts` — and it matters more here, not less.
       *
       * These posts are best-effort and swallow their own failures, so a dropped session cookie produces no
       * error anywhere: the datapoints simply stop arriving, and the dashboard reads as "nothing happened"
       * rather than "telemetry is broken".
       */
      credentials: 'include',
      /*
       * Survives unload, which is when these are most likely to be sent.
       *
       * The outcomes worth counting arrive at the end of the interaction — a submission, a skip, a link that
       * would not parse — and closing the tab immediately afterwards is the normal way to leave a
       * single-purpose page reached from a ticket. Without this the browser is free to cancel the in-flight
       * post, so the datapoints most at risk of being dropped are exactly the ones the Fill_Rate signal is
       * built from, and they would go missing in a way that reads as "nobody submitted".
       *
       * This is the guarantee `navigator.sendBeacon` provides, without a second transport: `keepalive` caps
       * the body at 64KB and these payloads are a handful of fields.
       */
      keepalive: true,
    });
    // Swallowed, not surfaced. A rejected telemetry post must not reach a React error boundary.
    if (request && typeof request.catch === 'function') {
      request.catch(() => undefined);
    }
  } catch {
    // Same reason. `fetch` can throw synchronously in some environments.
  }
}

/**
 * The default transport.
 *
 * `logPath` is sent in the *body* so the server can write the identifying values to a log record
 * without any of them becoming a metric dimension.
 */
export function createTelemetry(logPath?: string): MerlinFeedbackTelemetry {
  return {
    emitPageOutcome: (event) =>
      postBestEffort(PAGE_OUTCOME_ROUTE, { ...event, logPath }),
    emitContextResolution: (event) =>
      postBestEffort(CONTEXT_RESOLUTION_ROUTE, { ...event, logPath }),
  };
}

/** For tests and for hosts that have no transport. Emits nothing and never throws. */
export const noopTelemetry: MerlinFeedbackTelemetry = {
  emitPageOutcome: () => undefined,
  emitContextResolution: () => undefined,
};
